import { useToast, UseToastOptions } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { Navigate } from 'react-router-dom';

import { z, ZodError } from 'zod/v4';
import {
  errorToastConfig,
  errorToastId,
} from '~/packages/common-frontend/constants';

// Exports
export type ErrorBoundaryFallbackFn = (options: {
  error: unknown;
  message: string;
  showErrorToast: (toastText: string, options?: UseToastOptions) => void;
}) => React.ReactElement;
type ErrorBoundaryProps = {
  children: React.ReactNode;
  /** Rendered when an error is caught. Can take a ReactNode (JSX chuck), or a render function that gets access to the caught error. */
  fallback?: React.ReactNode | ErrorBoundaryFallbackFn;
  /** Reset the error boundary after the fallback is rendered/the fallback function is run. Useful for showing a toast, then immediately nagivating away. */
  resetAfterCatch?: boolean;
};
/**
 * Catches React render errors and displays fallback UI/runs fallback function. All caught errors
 * get logged to Sentry.
 *
 * See [React docs](https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary)
 * and [Sentry docs](https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/).
 */
export const ErrorBoundary = ({
  children,
  fallback = null,
  resetAfterCatch = false,
}: ErrorBoundaryProps) => {
  const toast = useToast();
  const resolvedFallback =
    typeof fallback === 'function' ? fallback : () => <>{fallback}</>;

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        // Let React get through a render cycle (for the fallback UI) before resetting the boundary
        if (resetAfterCatch) setTimeout(() => resetError(), 0);

        let message: string = 'Something went wrong';
        if (error instanceof ZodError) {
          message = z.prettifyError(error);
        } else if (error instanceof Error) {
          message = error.message;
        }

        return resolvedFallback({
          error,
          message,
          showErrorToast: (toastText, options) => {
            if (!toast.isActive(errorToastId)) {
              setTimeout(() => {
                toast({
                  ...errorToastConfig,
                  ...options,
                  title: toastText,
                });
              }, 0);
            }
          },
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export type RouteErrorBoundaryFallbackFn = ErrorBoundaryFallbackFn;
type RouteErrorBoundaryProps = ErrorBoundaryProps;
/**
 * Catches React render errors and displays fallback UI/runs fallback function. The same as
 * `<ErrorBoundary />`, but redirects to `/` and resets the boundary by default. All caught errors
 * get logged to Sentry.
 *
 * See [React docs](https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary)
 * and [Sentry docs](https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/).
 */
export const RouteErrorBoundary = ({
  children,
  fallback = <Navigate to="/" />,
  resetAfterCatch = true,
}: RouteErrorBoundaryProps) => (
  <ErrorBoundary fallback={fallback} resetAfterCatch={resetAfterCatch}>
    {children}
  </ErrorBoundary>
);
